/**
 * /lien-waivers/templates — 50-state statutory lien waiver template library.
 *
 * Left panel: searchable state list with statutory / notarization / witness /
 *             advance-waiver flags.
 * Right panel: 4-tab form viewer (Cond. Progress / Uncond. Progress /
 *              Cond. Final / Uncond. Final) with statutory text + copy button.
 *
 * All data is read-only reference. Template text is fetched on-demand and
 * cached indefinitely (static statutory content never changes).
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Skeleton,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import GavelIcon from "@mui/icons-material/Gavel";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import {
  lienWaiverTemplateStatesQueryOptions,
  lienWaiverTemplateQueryOptions,
} from "@/lib/queries";
import { useConstructionPack } from "@/lib/useConstructionPack";
import type { LienWaiverStateSummary } from "@/lib/types";

export const Route = createFileRoute("/_auth/lien-waivers_/templates")({
  component: LienWaiverTemplatesPage,
});

const FORM_TYPES = [
  { value: "conditional_progress", label: "Cond. Progress" },
  { value: "unconditional_progress", label: "Uncond. Progress" },
  { value: "conditional_final", label: "Cond. Final" },
  { value: "unconditional_final", label: "Uncond. Final" },
] as const;

type FormTypeValue = (typeof FORM_TYPES)[number]["value"];

// ── Page ──────────────────────────────────────────────────────────────────────

function LienWaiverTemplatesPage() {
  const { enabled: constructionEnabled } = useConstructionPack();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [selectedState, setSelectedState] =
    useState<LienWaiverStateSummary | null>(null);
  const [formTab, setFormTab] = useState<FormTypeValue>("conditional_progress");

  const statesQ = useQuery(lienWaiverTemplateStatesQueryOptions());

  function handleRefresh() {
    qc.invalidateQueries({ queryKey: ["lien-waivers", "templates"] });
  }

  const filteredStates = (statesQ.data ?? []).filter(
    (s) =>
      s.state.toLowerCase().includes(search.toLowerCase()) ||
      s.state_name.toLowerCase().includes(search.toLowerCase()),
  );

  if (!constructionEnabled) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-500 dark:text-slate-400">
        The Construction pack is not enabled for this entity.
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Toolbar */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800 flex items-center gap-3">
        <Tooltip title="Back to Lien Waivers">
          <IconButton
            component={Link}
            to="/lien-waivers"
            size="small"
            sx={{ mr: 0.5 }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <GavelIcon sx={{ color: "text.secondary", fontSize: 20 }} />
        <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
          50-State Template Library
        </h1>
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {statesQ.data?.length ?? 0} jurisdictions
        </span>
        <div className="flex-1" />
        <Tooltip title="Refresh template data">
          <IconButton
            size="small"
            onClick={handleRefresh}
            disabled={statesQ.isFetching}
          >
            <RefreshIcon
              fontSize="small"
              sx={{
                animation: statesQ.isFetching
                  ? "spin 1s linear infinite"
                  : "none",
                "@keyframes spin": { from: { rotate: "0deg" }, to: { rotate: "360deg" } },
              }}
            />
          </IconButton>
        </Tooltip>
      </div>

      {/* Body */}
      <div className="flex flex-1 overflow-hidden">
        {/* ── Left: state list ─────────────────────────────────────── */}
        <div className="w-80 shrink-0 border-r border-slate-200 dark:border-gray-800 flex flex-col bg-white dark:bg-gray-900">
          <div className="p-3 border-b border-slate-100 dark:border-gray-800">
            <TextField
              size="small"
              fullWidth
              placeholder="Search states…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <div className="flex-1 overflow-y-auto">
            {statesQ.isLoading &&
              Array.from({ length: 12 }).map((_, i) => (
                <div key={i} className="px-3 py-2">
                  <Skeleton width="60%" height={16} />
                  <Skeleton width="40%" height={12} sx={{ mt: 0.5 }} />
                </div>
              ))}

            {statesQ.isError && (
              <Alert severity="error" sx={{ m: 2 }}>
                Failed to load states
              </Alert>
            )}

            {filteredStates.map((s) => (
              <StateListItem
                key={s.state}
                summary={s}
                selected={selectedState?.state === s.state}
                onClick={() => {
                  setSelectedState(s);
                  setFormTab("conditional_progress");
                }}
              />
            ))}

            {!statesQ.isLoading && filteredStates.length === 0 && (
              <p className="text-center text-sm text-slate-400 py-8">
                No states match "{search}"
              </p>
            )}
          </div>
        </div>

        {/* ── Right: form viewer ───────────────────────────────────── */}
        <div className="flex-1 overflow-hidden flex flex-col bg-slate-50 dark:bg-gray-950">
          {!selectedState ? (
            <div className="flex items-center justify-center h-full text-slate-400 dark:text-slate-600 flex-col gap-2">
              <GavelIcon sx={{ fontSize: 48, opacity: 0.3 }} />
              <p className="text-sm">Select a state to view templates</p>
            </div>
          ) : (
            <FormViewer
              summary={selectedState}
              formTab={formTab}
              onFormTabChange={setFormTab}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ── StateListItem ──────────────────────────────────────────────────────────────

function StateListItem({
  summary,
  selected,
  onClick,
}: {
  summary: LienWaiverStateSummary;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={[
        "w-full text-left px-3 py-2.5 border-b border-slate-100 dark:border-gray-800",
        "hover:bg-slate-50 dark:hover:bg-gray-800 transition-colors",
        selected
          ? "bg-blue-50 dark:bg-blue-950 border-l-2 border-l-blue-500"
          : "",
      ].join(" ")}
    >
      <div className="flex items-center gap-2">
        <span className="font-mono text-xs font-semibold text-slate-500 dark:text-slate-400 w-7 shrink-0">
          {summary.state}
        </span>
        <span className="text-sm text-slate-800 dark:text-slate-200 truncate flex-1">
          {summary.state_name}
        </span>
        {summary.statutory && (
          <Chip label="Statutory" size="small" color="primary" variant="outlined"
            sx={{ fontSize: 10, height: 18, px: 0 }} />
        )}
      </div>
      <div className="flex gap-1.5 mt-1 ml-9">
        {summary.requires_notarization && (
          <span className="text-[10px] text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 rounded px-1">
            Notary
          </span>
        )}
        {summary.requires_witness && (
          <span className="text-[10px] text-purple-700 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/30 rounded px-1">
            Witness
          </span>
        )}
        {!summary.advance_waivers_allowed && (
          <span className="text-[10px] text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/30 rounded px-1">
            No advance waiver
          </span>
        )}
      </div>
    </button>
  );
}

// ── FormViewer ────────────────────────────────────────────────────────────────

function FormViewer({
  summary,
  formTab,
  onFormTabChange,
}: {
  summary: LienWaiverStateSummary;
  formTab: FormTypeValue;
  onFormTabChange: (v: FormTypeValue) => void;
}) {
  const [copied, setCopied] = useState(false);
  const tplQ = useQuery(lienWaiverTemplateQueryOptions(summary.state, formTab));

  function handleCopy() {
    if (!tplQ.data?.text) return;
    navigator.clipboard.writeText(tplQ.data.text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <>
      {/* Header */}
      <div className="px-6 pt-5 pb-3 bg-white dark:bg-gray-900 border-b border-slate-200 dark:border-gray-800">
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <Typography variant="h6" fontWeight={600}>
                {summary.state_name} ({summary.state})
              </Typography>
              {summary.statutory && (
                <Chip label="Statutory form" size="small" color="primary" />
              )}
            </div>
            {tplQ.data?.statute && (
              <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                {tplQ.data.statute}
                {tplQ.data.statute_section && ` · ${tplQ.data.statute_section}`}
              </Typography>
            )}
          </div>
        </div>

        {/* Requirement warnings */}
        <div className="flex gap-2 mt-3 flex-wrap">
          {summary.requires_notarization && (
            <div className="flex items-center gap-1 text-xs text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded px-2 py-1">
              <WarningAmberIcon sx={{ fontSize: 13 }} />
              Notarization required
            </div>
          )}
          {summary.requires_witness && (
            <div className="flex items-center gap-1 text-xs text-purple-800 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-800 rounded px-2 py-1">
              <WarningAmberIcon sx={{ fontSize: 13 }} />
              Witness signature required
            </div>
          )}
          {!summary.advance_waivers_allowed && (
            <div className="flex items-center gap-1 text-xs text-red-800 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded px-2 py-1">
              <WarningAmberIcon sx={{ fontSize: 13 }} />
              Advance waivers prohibited
            </div>
          )}
        </div>

        {summary.notes && (
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            mt={1.5}
            sx={{ fontStyle: "italic" }}
          >
            {summary.notes}
          </Typography>
        )}
      </div>

      {/* Form type tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "background.paper" }}>
        <Tabs
          value={formTab}
          onChange={(_, v) => onFormTabChange(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ minHeight: 40, "& .MuiTab-root": { minHeight: 40, py: 0, fontSize: 13 } }}
        >
          {FORM_TYPES.map(({ value, label }) => (
            <Tab key={value} value={value} label={label} />
          ))}
        </Tabs>
      </Box>

      {/* Template text */}
      <div className="flex-1 overflow-hidden flex flex-col px-6 py-4 gap-3">
        {tplQ.isLoading && (
          <div className="flex items-center gap-2 text-slate-400">
            <CircularProgress size={16} />
            <span className="text-sm">Loading template…</span>
          </div>
        )}

        {tplQ.isError && (
          <Alert severity="warning">
            No template available for this combination.
          </Alert>
        )}

        {tplQ.data && (
          <>
            {tplQ.data.title && (
              <Typography variant="subtitle2" fontWeight={600}>
                {tplQ.data.title}
              </Typography>
            )}

            {tplQ.data.notice && (
              <Alert severity="info" sx={{ py: 0.5 }}>
                {tplQ.data.notice}
              </Alert>
            )}

            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="flex items-center justify-between mb-1">
                <Typography variant="caption" color="text.secondary">
                  Statutory text
                  {tplQ.data.statutory ? " — use verbatim" : ""}
                </Typography>
                <Tooltip title={copied ? "Copied!" : "Copy text"}>
                  <IconButton size="small" onClick={handleCopy}>
                    <ContentCopyIcon
                      fontSize="small"
                      sx={{ color: copied ? "success.main" : "action.active" }}
                    />
                  </IconButton>
                </Tooltip>
              </div>
              <Divider sx={{ mb: 1 }} />
              <Box
                component="pre"
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  fontFamily: "monospace",
                  fontSize: 12,
                  lineHeight: 1.7,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  bgcolor: "background.paper",
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 2,
                  m: 0,
                  color: "text.primary",
                }}
              >
                {tplQ.data.text}
              </Box>
            </div>
          </>
        )}
      </div>
    </>
  );
}
